/**
 * BulkSetFieldAction
 *
 * Two-step field-then-value picker for bulk editing selected rows.
 * Step 1: Pick a field from editable fields list.
 * Step 2: Enter a value (type-appropriate input).
 * Confirmation dialog before applying.
 */

import { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Pencil, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { getERPClient } from '@/lib/erp/client';
import { useDebounce } from '@/lib/hooks/useDebounce';
import { parseLinkSearchResponse, type LinkSearchResult } from '@/lib/erp/utils/linkUtils';

type FieldMeta = { fieldtype: string; options?: string; label?: string };

interface BulkSetFieldActionProps {
  editableFields: Set<string>;
  fieldMetaMap: Map<string, FieldMeta>;
  selectedRowCount: number;
  onApply: (fieldname: string, value: unknown) => void;
}

export function BulkSetFieldAction({
  editableFields,
  fieldMetaMap,
  selectedRowCount,
  onApply,
}: BulkSetFieldActionProps) {
  const [fieldPopoverOpen, setFieldPopoverOpen] = useState(false);
  const [selectedField, setSelectedField] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState<unknown>('');
  const [showConfirm, setShowConfirm] = useState(false);

  // Build sorted field list from editableFields + fieldMetaMap
  const fieldList = useMemo(() => {
    const list: Array<{ fieldname: string; label: string; fieldtype: string; options?: string }> = [];
    for (const fieldname of editableFields) {
      const meta = fieldMetaMap.get(fieldname);
      if (meta) {
        list.push({
          fieldname,
          label: meta.label || fieldname,
          fieldtype: meta.fieldtype,
          options: meta.options,
        });
      }
    }
    return list.sort((a, b) => a.label.localeCompare(b.label));
  }, [editableFields, fieldMetaMap]);

  const selectedFieldMeta = selectedField ? fieldMetaMap.get(selectedField) : null;
  const selectedFieldLabel = selectedFieldMeta?.label || selectedField || '';

  const handleFieldSelect = useCallback((fieldname: string) => {
    setSelectedField(fieldname);
    setInputValue('');
    setFieldPopoverOpen(false);
  }, []);

  const handleApplyClick = useCallback(() => {
    if (!selectedField) return;
    setShowConfirm(true);
  }, [selectedField]);

  const handleConfirmApply = useCallback(() => {
    if (!selectedField) return;
    onApply(selectedField, inputValue);
    setShowConfirm(false);
    setSelectedField(null);
    setInputValue('');
  }, [selectedField, inputValue, onApply]);

  const handleCancel = useCallback(() => {
    setShowConfirm(false);
  }, []);

  const handleReset = useCallback(() => {
    setSelectedField(null);
    setInputValue('');
  }, []);

  const displayValue = inputValue === '' || inputValue === null || inputValue === undefined
    ? '(empty)'
    : String(inputValue);

  return (
    <>
      <div className="flex items-center gap-2">
        {/* Step 1: Field picker */}
        <Popover open={fieldPopoverOpen} onOpenChange={setFieldPopoverOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 gap-1.5">
              <Pencil className="h-3.5 w-3.5" />
              {selectedField ? selectedFieldLabel : 'Set field'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[200px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search fields..." className="h-8 text-sm" />
              <CommandList className="max-h-[240px]">
                <CommandEmpty>No fields found</CommandEmpty>
                <CommandGroup>
                  {fieldList.map((field) => (
                    <CommandItem
                      key={field.fieldname}
                      value={field.label}
                      onSelect={() => handleFieldSelect(field.fieldname)}
                      className="text-sm"
                    >
                      <Check
                        className={cn(
                          'mr-2 h-3.5 w-3.5 shrink-0',
                          selectedField === field.fieldname ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      {field.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Step 2: Value input (shown after field is selected) */}
        {selectedField && selectedFieldMeta && (
          <ValueInput
            fieldtype={selectedFieldMeta.fieldtype}
            options={selectedFieldMeta.options}
            value={inputValue}
            onChange={setInputValue}
          />
        )}

        {/* Apply button */}
        {selectedField && (
          <Button
            size="sm"
            className="h-8"
            onClick={handleApplyClick}
          >
            Apply
          </Button>
        )}

        {/* Reset button when field is selected */}
        {selectedField && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2"
            onClick={handleReset}
          >
            Cancel
          </Button>
        )}
      </div>

      {/* Confirmation dialog */}
      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm bulk update</AlertDialogTitle>
            <AlertDialogDescription>
              Set <strong>{selectedFieldLabel}</strong> to <strong>{displayValue}</strong> for{' '}
              <strong>{selectedRowCount}</strong> item{selectedRowCount !== 1 ? 's' : ''}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancel}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmApply}>Apply</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// --- Value Input Component ---

interface ValueInputProps {
  fieldtype: string;
  options?: string;
  value: unknown;
  onChange: (value: unknown) => void;
}

function ValueInput({ fieldtype, options, value, onChange }: ValueInputProps) {
  // Link field → standalone searchable picker
  if (fieldtype === 'Link' && options) {
    return (
      <LinkValueInput
        doctype={options}
        value={value as string}
        onChange={onChange}
      />
    );
  }

  // Select field → standalone option picker
  if (fieldtype === 'Select' && options) {
    return (
      <SelectValueInput
        options={options}
        value={value as string}
        onChange={onChange}
      />
    );
  }

  // Check field → checkbox
  if (fieldtype === 'Check') {
    return (
      <div className="flex items-center gap-2 h-8">
        <Checkbox
          checked={value === 1 || value === true}
          onCheckedChange={(checked) => onChange(checked ? 1 : 0)}
        />
        <span className="text-sm text-muted-foreground">
          {value === 1 || value === true ? 'Yes' : 'No'}
        </span>
      </div>
    );
  }

  // Number fields
  if (['Int', 'Float', 'Currency', 'Percent'].includes(fieldtype)) {
    return (
      <Input
        type="number"
        className="h-8 w-[140px] text-sm"
        value={String(value ?? '')}
        onChange={(e) => {
          const v = e.target.value;
          if (v === '') {
            onChange('');
          } else if (fieldtype === 'Int') {
            onChange(parseInt(v, 10) || 0);
          } else {
            onChange(parseFloat(v) || 0);
          }
        }}
        placeholder="Enter value..."
        autoFocus
      />
    );
  }

  // Default: text input
  return (
    <Input
      className="h-8 w-[180px] text-sm"
      value={String(value ?? '')}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Enter value..."
      autoFocus
    />
  );
}

// --- Link Value Input (standalone, not cell-anchored) ---

function LinkValueInput({
  doctype,
  value,
  onChange,
}: {
  doctype: string;
  value: string;
  onChange: (value: unknown) => void;
}) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 300);
  const searchText = debouncedSearch.trim().length >= 2 ? debouncedSearch.trim() : '';

  const { data: results = [], isLoading } = useQuery<LinkSearchResult[]>({
    queryKey: ['bulk-link-search', doctype, searchText],
    queryFn: async () => {
      const client = getERPClient();
      const response = await client.call<unknown>(
        'frappe.desk.search.search_link',
        { doctype, txt: searchText, page_length: 20 },
      );
      return parseLinkSearchResponse(response, { doctype });
    },
    enabled: open,
    staleTime: 30_000,
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 w-[180px] justify-start text-sm font-normal">
          {value || <span className="text-muted-foreground">Select...</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search..."
            value={searchTerm}
            onValueChange={setSearchTerm}
            className="h-8 text-sm"
            autoFocus
          />
          <CommandList className="max-h-[216px]">
            {isLoading && (
              <div className="p-1 space-y-1">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-[32px] rounded-sm bg-muted/50 animate-pulse mx-1" />
                ))}
              </div>
            )}
            {!isLoading && results.length === 0 && (
              <CommandEmpty>No results</CommandEmpty>
            )}
            {!isLoading && results.length > 0 && (
              <CommandGroup>
                {results.map((r) => (
                  <CommandItem
                    key={r.value}
                    value={r.value}
                    onSelect={() => {
                      onChange(r.value);
                      setOpen(false);
                    }}
                    className="text-sm px-2 py-1.5 min-h-[32px]"
                  >
                    <Check
                      className={cn(
                        'mr-2 h-3.5 w-3.5 shrink-0',
                        value === r.value ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <span className="truncate">{r.label || r.value}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// --- Select Value Input (standalone, not cell-anchored) ---

function SelectValueInput({
  options: rawOptions,
  value,
  onChange,
}: {
  options: string;
  value: string;
  onChange: (value: unknown) => void;
}) {
  const [open, setOpen] = useState(false);

  const parsedOptions = useMemo(() => {
    if (!rawOptions) return [];
    return rawOptions
      .split('\n')
      .map((s) => s.trim())
      .map((line) =>
        line === ''
          ? { label: '(None)', value: '' }
          : { label: line, value: line },
      );
  }, [rawOptions]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 w-[180px] justify-start text-sm font-normal">
          {value || <span className="text-muted-foreground">Select...</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Filter..." className="h-8 text-sm" autoFocus />
          <CommandList className="max-h-[216px]">
            <CommandEmpty>No match</CommandEmpty>
            <CommandGroup>
              {parsedOptions.map((opt) => (
                <CommandItem
                  key={opt.value || '__empty__'}
                  value={opt.label}
                  onSelect={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  className="text-sm px-2 py-1.5 min-h-[32px]"
                >
                  <Check
                    className={cn(
                      'mr-2 h-3.5 w-3.5 shrink-0',
                      value === opt.value ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className={cn(opt.value === '' && 'text-muted-foreground italic')}>
                    {opt.label}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
